<script lang="ts">
  import { FarmaciaClinica, Pacientes, Consultorios, downloadAuthenticatedFile, type ProductoFarmacia, type DocumentoFiscal,
    type ConsumoFel, type Receta, type RecetaItem, type CrearVentaPos } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';
  import {
    CATALOGO_MEDICAMENTOS_MSPAS, searchMedicamentosMspas, precioSugeridoClinica, type MedicamentoMspas,
  } from '../lib/farmacia-mspas-catalog';
  import { importMedicamentoToFarmacia, seedFarmaciaClinicaDemo } from '../lib/clinic-demo-seed';
  import { resolveMedicoId } from '../lib/medico-session';
  import ReciboImprimible from './ReciboImprimible.svelte';
  import { imprimirHtml, abrirOImprimirHtml } from '../lib/print-html';
  import HelpHint from './HelpHint.svelte';
  import { tenantCtx } from '../lib/tenant.svelte';
  import { effectiveOrgTipo } from '../lib/org-context.svelte';
  import { isClinicOrgTipo } from '../lib/navigation';
  import { parseSpreadsheet, csvCell } from '../lib/csv-import';

  type SeccionFarmacia = 'pos' | 'recetas' | 'inventario' | 'catalogo' | 'documentos';
  let seccion = $state<SeccionFarmacia>('pos');

  const esClinicaPrivada = $derived(
    tenantCtx.isClinicEdition
      || isClinicOrgTipo(effectiveOrgTipo(tenantCtx.orgTipo as import('../lib/navigation').OrgTipo | null, tenantCtx.isHospitalEdition)),
  );

  let error = $state<string|null>(null);
  let info = $state<string|null>(null);
  let busquedaCatalogo = $state('');
  let busquedaPos = $state('');
  let busquedaInventario = $state('');
  let verInactivosInventario = $state(false);
  let sembrando = $state(false);

  const productosInventarioFiltrados = $derived(
    productos
      .filter(p => verInactivosInventario || p.activo)
      .filter(p =>
        !busquedaInventario.trim()
        || p.nombre.toLowerCase().includes(busquedaInventario.toLowerCase())
        || (p.codigo ?? '').toLowerCase().includes(busquedaInventario.toLowerCase()),
      )
  );

  const resultadosCatalogo = $derived(searchMedicamentosMspas(busquedaCatalogo, 20));

  // ── Catálogo ───────────────────────────────────────────────────────────────
  let productos = $state<ProductoFarmacia[]>([]);
  let nuevo = $state<ProductoFarmacia>({ codigo:'', nombre:'', categoria:'MEDICAMENTO', precio:0, stock:0, unidad:'', requiereReceta:false, afectaInventario:true, activo:true });

  const productosActivosFiltrados = $derived(
    productos.filter(p => p.activo).filter(p =>
      !busquedaPos
      || p.nombre.toLowerCase().includes(busquedaPos.toLowerCase())
      || (p.codigo ?? '').toLowerCase().includes(busquedaPos.toLowerCase()),
    ),
  );

  async function cargarProductos() {
    try { productos = await FarmaciaClinica.productos(); } catch (e: any) { error = e.message; }
  }
  async function crearProducto() {
    error = null;
    if (!nuevo.codigo || !nuevo.nombre) { error = 'Código y nombre del producto son requeridos'; return; }
    try {
      await FarmaciaClinica.crearProducto({ ...nuevo, unidad: nuevo.unidad || null });
      nuevo = { codigo:'', nombre:'', categoria:'MEDICAMENTO', precio:0, stock:0, unidad:'', requiereReceta:false, afectaInventario:true, activo:true };
      await cargarProductos();
    } catch (e: any) { error = e.message; }
  }
  async function toggleProducto(p: ProductoFarmacia) {
    try { await FarmaciaClinica.actualizarProducto(p.id!, { ...p, activo: !p.activo }); await cargarProductos(); }
    catch (e: any) { error = e.message; }
  }

  async function guardarProductoFila(p: ProductoFarmacia) {
    error = null;
    try {
      await FarmaciaClinica.actualizarProducto(p.id!, {
        ...p,
        precio: Number(p.precio),
        stock: Number(p.stock),
      });
      info = `Producto ${p.nombre} actualizado.`;
      await cargarProductos();
    } catch (e: any) { error = e.message; }
  }

  async function importarCsvInventario(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    error = null;
    try {
      const { rows } = await parseSpreadsheet(file);
      let creados = 0;
      let actualizados = 0;
      for (const row of rows) {
        const codigo = csvCell(row, 'codigo', 'code');
        const nombre = csvCell(row, 'nombre', 'name');
        if (!codigo || !nombre) continue;
        const precio = Number(csvCell(row, 'precio', 'price') || 0);
        const stock = Number(csvCell(row, 'stock', 'existencia') || 0);
        const categoria = (csvCell(row, 'categoria', 'category') || 'MEDICAMENTO').toUpperCase() as ProductoFarmacia['categoria'];
        const unidad = csvCell(row, 'unidad', 'unit') || null;
        const cats: ProductoFarmacia['categoria'][] = ['MEDICAMENTO', 'INSUMO', 'PRODUCTO', 'SERVICIO', 'OTRO'];
        const cat = cats.includes(categoria) ? categoria : 'MEDICAMENTO';
        const existente = productos.find(p => p.codigo.toLowerCase() === codigo.toLowerCase());
        if (existente?.id) {
          await FarmaciaClinica.actualizarProducto(existente.id, {
            ...existente, nombre, precio, stock, categoria: cat, unidad, activo: true,
          });
          actualizados++;
        } else {
          await FarmaciaClinica.crearProducto({
            codigo, nombre, categoria: cat, precio, stock, unidad,
            requiereReceta: false, afectaInventario: true, activo: true,
          });
          creados++;
        }
      }
      await cargarProductos();
      info = `Importación inventario: ${creados} creados, ${actualizados} actualizados.`;
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      input.value = '';
    }
  }

  async function importarMedicamento(m: MedicamentoMspas) {
    error = null;
    const r = await importMedicamentoToFarmacia(m.codigo);
    info = r.message;
    if (!r.ok) error = r.message;
    else await cargarProductos();
  }

  async function sembrarCatalogoNacional() {
    sembrando = true;
    error = null;
    const r = await seedFarmaciaClinicaDemo();
    info = r.ok ? `${r.message}${r.details?.length ? ' · ' + r.details.join(', ') : ''}` : null;
    if (!r.ok) error = r.message;
    else await cargarProductos();
    sembrando = false;
  }

  // ── Punto de venta ───────────────────────────────────────────────────────────
  type LineaCarrito = { productoId?: string; descripcion: string; cantidad: number; precioUnitario: number; requiereReceta?: boolean };
  let carrito = $state<LineaCarrito[]>([]);
  let pacienteId = $state('');
  let formaPago = $state<'EFECTIVO'|'TARJETA'|'TRANSFERENCIA'|'CUENTA'>('EFECTIVO');
  let quiereFactura = $state(false);
  let receptorNit = $state('CF');
  let receptorNombre = $state('Consumidor Final');
  let recetaSeleccionada = $state<string>('');
  let ultimoDocumento = $state<DocumentoFiscal|null>(null);
  let lineasImpresion = $state<Array<{ descripcion: string; cantidad: number; precioUnitario: number; subtotal?: number }>>([]);
  let pacienteNombreVenta = $state('');
  let cobrando = $state(false);
  let despachando = $state(false);

  const total = $derived(carrito.reduce((s, l) => s + (Number(l.cantidad)||0) * (Number(l.precioUnitario)||0), 0));
  const requiereReceta = $derived(carrito.some(l => l.requiereReceta));

  function agregarProducto(p: ProductoFarmacia) {
    const existe = carrito.find(l => l.productoId === p.id);
    if (existe) { existe.cantidad += 1; }
    else carrito.push({ productoId: p.id, descripcion: p.nombre, cantidad: 1, precioUnitario: p.precio, requiereReceta: p.requiereReceta });
  }
  function agregarLibre() { carrito.push({ descripcion: '', cantidad: 1, precioUnitario: 0 }); }
  function quitar(i: number) { carrito.splice(i, 1); }
  function limpiarVenta() { carrito = []; recetaSeleccionada = ''; ultimoDocumento = null; lineasImpresion = []; pacienteNombreVenta = ''; }

  async function cobrar() {
    error = null; info = null; ultimoDocumento = null;
    if (carrito.length === 0) { error = 'El carrito está vacío'; return; }
    if (requiereReceta && !recetaSeleccionada) { error = 'Hay medicamentos que requieren receta. Selecciona o crea una receta.'; return; }
    cobrando = true;
    try {
      const body: CrearVentaPos = {
        pacienteId: pacienteId || undefined,
        recetaId: recetaSeleccionada || undefined,
        formaPago,
        quiereFactura,
        receptorNit: quiereFactura ? (receptorNit || 'CF') : undefined,
        receptorNombre: quiereFactura ? (receptorNombre || 'Consumidor Final') : undefined,
        items: carrito.map(l => ({ productoId: l.productoId, descripcion: l.descripcion, cantidad: Number(l.cantidad), precioUnitario: Number(l.precioUnitario) })),
      };
      lineasImpresion = carrito.map(l => ({
        descripcion: l.descripcion,
        cantidad: Number(l.cantidad),
        precioUnitario: Number(l.precioUnitario),
        subtotal: Number(l.cantidad) * Number(l.precioUnitario),
      }));
      if (pacienteId) {
        try {
          const p = await Pacientes.uno(pacienteId);
          pacienteNombreVenta = [p.nombres, p.apellidos].filter(Boolean).join(' ').trim();
        } catch { pacienteNombreVenta = ''; }
      }
      const r = await FarmaciaClinica.cobrar(body);
      ultimoDocumento = r.documento;
      info = `${r.documento.tipo === 'FACTURA' ? 'Factura' : 'Recibo'} ${r.documento.serie}-${r.documento.numero} emitido por Q${r.documento.total}.`;
      carrito = []; recetaSeleccionada = '';
      await cargarProductos();
      await cargarDocumentos();
      await cargarConsumo();
    } catch (e: any) { error = e.message; }
    cobrando = false;
  }

  async function enviarACobros() {
    error = null; info = null;
    if (carrito.length === 0) { error = 'El carrito está vacío'; return; }
    if (!pacienteId) { error = 'Selecciona un paciente para enviar a cobros.'; return; }
    if (requiereReceta && !recetaSeleccionada) { error = 'Hay medicamentos que requieren receta. Selecciona o crea una receta.'; return; }
    despachando = true;
    try {
      const body: CrearVentaPos = {
        pacienteId,
        recetaId: recetaSeleccionada || undefined,
        formaPago,
        quiereFactura: false,
        notas: 'Despacho → cobros secretaría',
        items: carrito.map(l => ({ productoId: l.productoId, descripcion: l.descripcion, cantidad: Number(l.cantidad), precioUnitario: Number(l.precioUnitario) })),
      };
      const r = await FarmaciaClinica.despachar(body);
      info = `Despachado por Q${Number(r.venta.total ?? 0).toFixed(2)} · pendiente en Cobros (secretaría).`;
      carrito = []; recetaSeleccionada = '';
      await cargarProductos();
    } catch (e: any) { error = e.message; }
    despachando = false;
  }

  // ── Recetas ──────────────────────────────────────────────────────────────────
  let recetas = $state<Receta[]>([]);
  let recetaPacienteId = $state('');
  let recetaDiagnostico = $state('');
  let recetaItems = $state<RecetaItem[]>([]);
  let filtroMesRecetas = $state(new Date().toISOString().slice(0, 7));
  let mostrarAnuladas = $state(false);
  let busquedaReceta = $state('');

  const recetasFiltradas = $derived(
    recetas.filter(r => {
      if (!mostrarAnuladas && r.estado === 'ANULADA') return false;
      if (filtroMesRecetas && r.fecha && !r.fecha.startsWith(filtroMesRecetas)) return false;
      if (busquedaReceta.trim()) {
        const q = busquedaReceta.toLowerCase();
        if (!(r.diagnostico ?? '').toLowerCase().includes(q)) return false;
      }
      return true;
    })
  );

  async function cargarRecetas() {
    try { recetas = await FarmaciaClinica.recetas(); } catch (e: any) { error = e.message; }
  }
  function agregarItemReceta() { recetaItems.push({ descripcion:'', cantidad:1, indicaciones:'' }); }
  function quitarItemReceta(i: number) { recetaItems.splice(i, 1); }
  async function crearReceta() {
    error = null;
    if (!recetaPacienteId) { error = 'La receta requiere un paciente'; return; }
    if (recetaItems.length === 0) { error = 'Agrega al menos un medicamento a la receta'; return; }
    try {
      let medicoId = await resolveMedicoId();
      if (!medicoId) {
        const profs = await Consultorios.profesionales(true).catch(() => []);
        medicoId = profs[0]?.id;
      }
      await FarmaciaClinica.crearReceta({
        pacienteId: recetaPacienteId,
        medicoId: medicoId ?? null,
        diagnostico: recetaDiagnostico || null,
        items: recetaItems.map(it => ({ ...it, cantidad: Number(it.cantidad) })),
      });
      recetaPacienteId = ''; recetaDiagnostico = ''; recetaItems = [];
      await cargarRecetas();
      info = 'Receta emitida.';
    } catch (e: any) { error = e.message; }
  }
  function cargarRecetaEnPos(r: Receta) {
    carrito = (r.items ?? []).map(it => {
      const desc = (it.descripcion ?? '').toLowerCase();
      const prod = (it.productoId ? productos.find(p => p.id === it.productoId) : undefined)
        ?? productos.find(p =>
          !!desc && (p.nombre.toLowerCase().includes(desc) || desc.includes(p.nombre.toLowerCase())),
        );
      return {
        productoId: prod?.id ?? it.productoId ?? undefined,
        descripcion: it.descripcion,
        cantidad: Number(it.cantidad) || 1,
        precioUnitario: prod ? prod.precio : 0,
        requiereReceta: prod?.requiereReceta,
      };
    });
    recetaSeleccionada = r.id!;
    if (r.pacienteId) pacienteId = r.pacienteId;
    seccion = 'pos';
    info = 'Receta cargada en POS — revise precios y cobre';
  }
  async function anularReceta(r: Receta) {
    try { await FarmaciaClinica.anularReceta(r.id!); await cargarRecetas(); } catch (e: any) { error = e.message; }
  }

  function abrirHtml(html: string) {
    abrirOImprimirHtml(html);
  }

  async function datosPacienteReceta(r: Receta) {
    let pacienteNombre = '';
    let pacienteDocumento = '';
    if (r.pacienteId) {
      try {
        const p = await Pacientes.uno(r.pacienteId);
        pacienteNombre = [p.nombres, p.apellidos].filter(Boolean).join(' ').trim();
        pacienteDocumento = p.cui ?? '';
      } catch { /* optional */ }
    }
    return { pacienteNombre, pacienteDocumento };
  }

  async function imprimirReceta(r: Receta) {
    error = null;
    const { pacienteNombre, pacienteDocumento } = await datosPacienteReceta(r);
    try {
      await downloadAuthenticatedFile(
        FarmaciaClinica.recetaPdf(r.id!, pacienteNombre, pacienteDocumento),
        `receta-${r.id}.pdf`,
      );
    } catch {
      try {
        imprimirHtml(await FarmaciaClinica.recetaImprimir(r.id!, pacienteNombre, pacienteDocumento));
      } catch (e: any) {
        error = e?.message ?? 'No se pudo imprimir la receta.';
      }
    }
  }

  async function verReceta(r: Receta) {
    error = null;
    const { pacienteNombre, pacienteDocumento } = await datosPacienteReceta(r);
    try {
      abrirHtml(await FarmaciaClinica.recetaImprimir(r.id!, pacienteNombre, pacienteDocumento));
    } catch (e: any) {
      error = e.message;
    }
  }

  // ── Documentos + consumo FEL ──────────────────────────────────────────────────
  let documentos = $state<DocumentoFiscal[]>([]);
  let consumo = $state<ConsumoFel|null>(null);
  const hoy = new Date();
  const primero = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const iso = (d: Date) => d.toISOString().slice(0,10);
  let desde = $state(iso(primero));
  let hasta = $state(iso(hoy));

  async function cargarDocumentos() {
    try { documentos = await FarmaciaClinica.documentos(); } catch (e: any) { error = e.message; }
  }
  async function cargarConsumo() {
    try { consumo = await FarmaciaClinica.consumoFel(desde, hasta); } catch (e: any) { error = e.message; }
  }

  let descargandoPdfId = $state<string | null>(null);

  async function descargarPdfDocumento(d: DocumentoFiscal) {
    if (!d.id) return;
    descargandoPdfId = d.id;
    error = null;
    try {
      await FarmaciaClinica.descargarPdf(d.id, `factura-${d.serie}-${d.numero}.pdf`);
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      descargandoPdfId = null;
    }
  }

  let felAccionId = $state<string | null>(null);

  async function reintentarFelDocumento(d: DocumentoFiscal) {
    if (!d.id) return;
    felAccionId = d.id;
    error = null;
    try {
      await FarmaciaClinica.reintentarFel(d.id);
      info = `Factura ${d.serie}-${d.numero} re-certificada.`;
      await cargarDocumentos();
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      felAccionId = null;
    }
  }

  async function anularFelDocumento(d: DocumentoFiscal) {
    if (!d.id || !confirm(`¿Anular factura ${d.serie}-${d.numero}?`)) return;
    felAccionId = d.id;
    error = null;
    try {
      await FarmaciaClinica.anularFel(d.id, 'Anulación desde mostrador');
      info = `Factura ${d.serie}-${d.numero} anulada.`;
      await cargarDocumentos();
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      felAccionId = null;
    }
  }

  $effect(() => { cargarProductos(); cargarRecetas(); cargarDocumentos(); cargarConsumo(); });
</script>

<section>
  <header class="view-header">
    <div>
      <h2>Farmacia POS</h2>
      {#if esClinicaPrivada}
        <p class="muted">Despacho de medicamentos; secretaría cierra el pago en Cobros.</p>
      {:else}
        <p class="muted">Venda medicamentos en el mostrador. Consultas y laboratorio se cobran en <strong>Cobrar</strong>.</p>
      {/if}
      {#if esClinicaPrivada}
      <HelpHint title="Ayuda · Farmacia POS">
        <ol>
          <li><strong>POS:</strong> busque el producto, agréguelo al carrito y cobre o despache.</li>
          <li><strong>Inventario:</strong> alta manual o <strong>subir CSV/Excel</strong> con columnas codigo, nombre, precio, stock, categoria, unidad.</li>
          <li><strong>Catálogo MSPAS:</strong> copie medicamentos oficiales al inventario con «+ Inventario».</li>
        </ol>
      </HelpHint>
      {/if}
    </div>
  </header>

  <nav class="tabs">
    <button type="button" class:active={seccion === 'pos'} onclick={() => seccion = 'pos'}>
      {esClinicaPrivada ? 'Despachar' : 'Vender'}
    </button>
    <button type="button" class:active={seccion === 'recetas'} onclick={() => seccion = 'recetas'}>Recetas</button>
    <button type="button" class:active={seccion === 'inventario'} onclick={() => seccion = 'inventario'}>Inventario</button>
    {#if esClinicaPrivada}
      <button type="button" class:active={seccion === 'catalogo'} onclick={() => seccion = 'catalogo'}>Catálogo MSPAS</button>
    {:else}
      <button type="button" class:active={seccion === 'catalogo'} onclick={() => seccion = 'catalogo'}>Catálogo</button>
    {/if}
    <button type="button" class:active={seccion === 'documentos'} onclick={() => seccion = 'documentos'}>Recibos</button>
  </nav>

  {#if error}<div class="err">⚠ {error}</div>{/if}
  {#if info}<div class="ok">{info}</div>{/if}

  {#if seccion === 'pos'}
    <div class="cols">
      <div class="panel col catalogo-pos">
        <div class="panel-head">
          <h3>Catálogo</h3>
          <input placeholder="Buscar producto…" bind:value={busquedaPos} />
        </div>
        <div class="catalogo-scroll">
          <table class="catalogo-tabla">
            <thead><tr><th>Código</th><th>Descripción</th><th class="num">Precio</th><th class="num">Stock</th><th></th></tr></thead>
            <tbody>
              {#each productosActivosFiltrados as p (p.id)}
                <tr>
                  <td class="mono">{p.codigo}</td>
                  <td>{p.nombre}{p.requiereReceta ? ' ℞' : ''}</td>
                  <td class="num">Q{p.precio}</td>
                  <td class="num">{p.afectaInventario ? p.stock : '—'}</td>
                  <td><button onclick={() => agregarProducto(p)}>Agregar</button></td>
                </tr>
              {/each}
              {#if productosActivosFiltrados.length === 0}<tr><td colspan="5" class="hint">Sin productos. Créalos en Inventario.</td></tr>{/if}
            </tbody>
          </table>
        </div>
        <button class="link" onclick={agregarLibre}>+ Ítem libre</button>
      </div>

      <div class="panel col carrito">
        <h3>Carrito</h3>
        {#if !esClinicaPrivada}
          <p class="hint">Producto → carrito → Cobrar. Queda el recibo.</p>
        {/if}
        <table>
          <thead><tr><th>Descripción</th><th>Cant.</th><th>P. unit.</th><th>Subtotal</th><th></th></tr></thead>
          <tbody>
            {#each carrito as l, i (i)}
              <tr>
                <td><input bind:value={l.descripcion} placeholder="Descripción" />{l.requiereReceta ? ' ℞' : ''}</td>
                <td><input type="number" min="0" step="0.001" bind:value={l.cantidad} class="num" /></td>
                <td><input type="number" min="0" step="0.01" bind:value={l.precioUnitario} class="num" /></td>
                <td class="num">Q{((Number(l.cantidad)||0)*(Number(l.precioUnitario)||0)).toFixed(2)}</td>
                <td><button onclick={() => quitar(i)}>×</button></td>
              </tr>
            {/each}
            {#if carrito.length === 0}<tr><td colspan="5" class="hint">Carrito vacío. Agrega productos del catálogo.</td></tr>{/if}
          </tbody>
        </table>

        <div class="resumen"><span>Total: <strong>Q{total.toFixed(2)}</strong></span>{#if requiereReceta}<span class="receta-flag">Requiere receta ℞</span>{/if}</div>

        <div class="form">
          <label class="field" style="flex:1;min-width:16rem;"><span>Paciente {esClinicaPrivada ? '' : '(opcional)'}</span>
            <PacientePicker bind:value={pacienteId} onPick={(id) => pacienteId = id} placeholder={esClinicaPrivada ? 'Paciente del despacho…' : 'Cliente de mostrador o buscar paciente…'} />
          </label>
          <label class="field"><span>Forma de pago</span>
            <select bind:value={formaPago}>
              <option value="EFECTIVO">Efectivo</option>
              <option value="TARJETA">Tarjeta</option>
              <option value="TRANSFERENCIA">Transferencia</option>
            </select>
          </label>
          {#if requiereReceta}
            <label class="field"><span>Receta</span>
              <select bind:value={recetaSeleccionada}>
                <option value="">— selecciona —</option>
                {#each recetas.filter(r => r.estado === 'EMITIDA') as r (r.id)}
                  <option value={r.id}>{r.diagnostico ?? 'Receta'} · {r.fecha?.slice(0,10)}</option>
                {/each}
              </select>
            </label>
          {/if}
        </div>

        {#if !esClinicaPrivada}
          <div class="factura-box">
            <label class="switch">
              <input type="checkbox" bind:checked={quiereFactura} />
              <span>¿Quiere factura? {quiereFactura ? '(Factura electrónica FEL)' : '(Recibo)'}</span>
            </label>
            {#if quiereFactura}
              <div class="form">
                <label class="field"><span>NIT</span><input bind:value={receptorNit} placeholder="CF" /></label>
                <label class="field" style="flex:1;min-width:14rem;"><span>Nombre</span><input bind:value={receptorNombre} placeholder="Consumidor Final" /></label>
              </div>
            {/if}
          </div>
        {/if}

        <div class="acciones">
          {#if esClinicaPrivada}
            <button class="primary" onclick={enviarACobros} disabled={despachando || carrito.length === 0 || !pacienteId}>
              {despachando ? 'Enviando…' : `Enviar a cobros · Q${total.toFixed(2)}`}
            </button>
          {:else}
            <button class="primary" onclick={cobrar} disabled={cobrando || carrito.length === 0}>{cobrando ? 'Cobrando…' : `Cobrar Q${total.toFixed(2)}`}</button>
          {/if}
          <button class="ghost" onclick={limpiarVenta}>Limpiar</button>
        </div>

        {#if ultimoDocumento}
          <ReciboImprimible
            documento={ultimoDocumento}
            pacienteNombre={pacienteNombreVenta}
            lineas={lineasImpresion}
            onClose={() => { ultimoDocumento = null; lineasImpresion = []; }}
          />
        {/if}
      </div>
    </div>
  {/if}

  {#if seccion === 'recetas'}
    <div class="panel">
      <h3>Emitir receta médica</h3>
      <p class="hint">El médico emite la receta; la farmacia la carga en el POS. Los productos marcados con ℞ exigen receta.</p>
      <div class="form">
        <label class="field" style="flex:1;min-width:16rem;"><span>Paciente</span>
          <PacientePicker bind:value={recetaPacienteId} onPick={(id) => recetaPacienteId = id} placeholder="Buscar paciente…" />
        </label>
        <label class="field" style="flex:1;min-width:14rem;"><span>Diagnóstico</span><input bind:value={recetaDiagnostico} placeholder="Diagnóstico (opcional)" /></label>
        <button onclick={agregarItemReceta}>+ Medicamento</button>
        <button class="primary" onclick={crearReceta}>Emitir receta</button>
      </div>
      {#if recetaItems.length > 0}
        <table>
          <thead><tr><th>Medicamento</th><th>Cant.</th><th>Indicaciones</th><th></th></tr></thead>
          <tbody>
            {#each recetaItems as it, i (i)}
              <tr>
                <td><input bind:value={it.descripcion} placeholder="Medicamento" /></td>
                <td><input type="number" min="0" step="0.001" bind:value={it.cantidad} class="num" /></td>
                <td><input bind:value={it.indicaciones} placeholder="1 tableta c/8h por 7 días" /></td>
                <td><button onclick={() => quitarItemReceta(i)}>×</button></td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>

    <h3>Recetas</h3>
    <div class="filtros-recetas row">
      <label class="field"><span>Mes</span>
        <input type="month" bind:value={filtroMesRecetas} />
      </label>
      <label class="field grow"><span>Buscar</span>
        <input type="search" placeholder="Diagnóstico…" bind:value={busquedaReceta} />
      </label>
      <label class="field chk"><span>&nbsp;</span>
        <label class="inline-chk"><input type="checkbox" bind:checked={mostrarAnuladas} /> Ver anuladas</label>
      </label>
    </div>
    <table>
      <thead><tr><th>Fecha</th><th>Diagnóstico</th><th>Renglones</th><th>Estado</th><th></th></tr></thead>
      <tbody>
        {#each recetasFiltradas as r (r.id)}
          <tr class:inactiva={r.estado === 'ANULADA'}>
            <td>{r.fecha?.slice(0,10)}</td>
            <td>{r.diagnostico ?? '—'}</td>
            <td>{r.items?.length ?? 0}</td>
            <td>{r.estado}</td>
            <td>
              {#if r.estado === 'EMITIDA'}<button onclick={() => cargarRecetaEnPos(r)}>Cargar en POS</button>{/if}
              {#if r.estado !== 'ANULADA'}<button onclick={() => verReceta(r)}>Vista previa</button>{/if}
              {#if r.estado !== 'ANULADA'}<button onclick={() => imprimirReceta(r)}>Imprimir ℞</button>{/if}
              {#if r.estado === 'EMITIDA'}<button onclick={() => anularReceta(r)}>Anular</button>{/if}
            </td>
          </tr>
        {/each}
        {#if recetasFiltradas.length === 0}<tr><td colspan="5">Sin recetas para este filtro.</td></tr>{/if}
      </tbody>
    </table>
  {/if}

  {#if seccion === 'catalogo'}
    <div class="panel">
      <h3>Catálogo nacional MSPAS · medicamentos Guatemala</h3>
      <p class="hint">{CATALOGO_MEDICAMENTOS_MSPAS.length} medicamentos oficiales (códigos A001…V012). Referencia del catálogo nacional; al seleccionar uno pasa a su inventario de mostrador para cobrar en POS.</p>
      <div class="form cat-bar">
        <input placeholder="Buscar por código (A004) o nombre (LOSARTAN…)" bind:value={busquedaCatalogo} style="flex:2;min-width:14rem;" />
        <button type="button" disabled={sembrando} onclick={sembrarCatalogoNacional}>
          {sembrando ? 'Sembrando…' : 'Cargar top 40 al inventario'}
        </button>
      </div>
    </div>
    <table>
      <thead><tr><th>Código</th><th>Descripción</th><th class="num">Precio</th><th></th></tr></thead>
      <tbody>
        {#each resultadosCatalogo as m (m.codigo)}
          <tr>
            <td class="mono">{m.codigo}</td>
            <td>{m.nombre}</td>
            <td class="num">Q {precioSugeridoClinica(m).toFixed(2)}</td>
            <td><button type="button" onclick={() => importarMedicamento(m)}>+ Inventario</button></td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}

  {#if seccion === 'inventario'}
    <div class="panel">
      <h3>Carga masiva</h3>
      <div class="csv-row excel-import" style="margin-top:0;padding-top:0;border-top:0">
        <label class="field">
          <span>Subir archivo Excel / CSV</span>
          <input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onchange={importarCsvInventario} />
        </label>
        <span class="csv-hint">Columnas: codigo, nombre, precio, stock, categoria, unidad</span>
      </div>
      <h3>Nuevo producto</h3>
      <div class="form">
        <label class="field"><span>Código</span><input placeholder="Código" bind:value={nuevo.codigo} /></label>
        <label class="field"><span>Nombre</span><input placeholder="Nombre" bind:value={nuevo.nombre} /></label>
        <label class="field"><span>Categoría</span>
          <select bind:value={nuevo.categoria}>
            <option value="MEDICAMENTO">Medicamento</option>
            <option value="INSUMO">Insumo</option>
            <option value="PRODUCTO">Producto</option>
            <option value="SERVICIO">Servicio</option>
            <option value="OTRO">Otro</option>
          </select>
        </label>
        <label class="field"><span>Precio (Q)</span><input type="number" min="0" step="0.01" bind:value={nuevo.precio} class="num" /></label>
        <label class="field"><span>Stock</span><input type="number" min="0" step="1" bind:value={nuevo.stock} class="num" /></label>
        <label class="field"><span>Unidad</span><input placeholder="Unidad" bind:value={nuevo.unidad} class="num" /></label>
        <label class="switch"><input type="checkbox" bind:checked={nuevo.requiereReceta} /> Requiere receta</label>
        <label class="switch"><input type="checkbox" bind:checked={nuevo.afectaInventario} /> Afecta inventario</label>
        <button onclick={crearProducto}>Agregar</button>
      </div>
    </div>

    <h3>Inventario POS</h3>
    <div class="filtros-recetas row">
      <label class="field grow"><span>Buscar</span>
        <input type="search" placeholder="Nombre o código…" bind:value={busquedaInventario} />
      </label>
      <label class="field chk"><span>&nbsp;</span>
        <label class="inline-chk"><input type="checkbox" bind:checked={verInactivosInventario} /> Ver inactivos</label>
      </label>
    </div>
    <table>
      <thead><tr><th>Código</th><th>Nombre</th><th>Categoría</th><th>Precio</th><th>Stock</th><th>℞</th><th>Inv.</th><th>Activo</th><th></th></tr></thead>
      <tbody>
        {#each productosInventarioFiltrados as p (p.id)}
          <tr class:inactiva={!p.activo}>
            <td>{p.codigo}</td>
            <td>{p.nombre}</td>
            <td>{p.categoria}</td>
            <td><input type="number" min="0" step="0.01" bind:value={p.precio} class="num" /></td>
            <td>
              {#if p.afectaInventario}
                <input type="number" min="0" step="1" bind:value={p.stock} class="num" />
              {:else}
                —
              {/if}
            </td>
            <td>{p.requiereReceta ? 'Sí' : '—'}</td>
            <td>{p.afectaInventario ? 'Sí' : 'No'}</td>
            <td>{p.activo ? 'Sí' : 'No'}</td>
            <td>
              <button type="button" onclick={() => guardarProductoFila(p)}>Guardar</button>
              <button type="button" onclick={() => toggleProducto(p)}>{p.activo ? 'Desactivar' : 'Activar'}</button>
            </td>
          </tr>
        {/each}
        {#if productos.length === 0}<tr><td colspan="9">Sin productos.</td></tr>{/if}
      </tbody>
    </table>
  {/if}

  {#if seccion === 'documentos'}
    <h3>Documentos emitidos</h3>
    <table>
      <thead><tr><th>Fecha</th><th>Tipo</th><th>Doc.</th><th>Receptor</th><th>Total</th><th>IVA</th><th>Estado</th><th>FEL</th><th></th></tr></thead>
      <tbody>
        {#each documentos as d (d.id)}
          <tr>
            <td>{d.fecha?.slice(0,10)}</td>
            <td>{d.tipo}</td>
            <td>{d.serie}-{d.numero}</td>
            <td>{d.tipo === 'FACTURA' ? `${d.receptorNombre} (${d.receptorNit})` : '—'}</td>
            <td>Q{d.total}</td>
            <td>{d.tipo === 'FACTURA' ? `Q${d.iva}` : '—'}</td>
            <td>{d.estado}</td>
            <td>{d.felAutorizacion ?? '—'}</td>
            <td>
              {#if d.tipo === 'FACTURA' && d.id}
                {#if d.estado === 'CERTIFICADO'}
                  <button type="button" class="link" onclick={() => descargarPdfDocumento(d)} disabled={descargandoPdfId === d.id}>
                    {descargandoPdfId === d.id ? '…' : 'PDF'}
                  </button>
                  <button type="button" class="link" onclick={() => anularFelDocumento(d)} disabled={felAccionId === d.id}>Anular</button>
                {:else if d.estado === 'EMITIDO'}
                  <button type="button" class="link" onclick={() => reintentarFelDocumento(d)} disabled={felAccionId === d.id}>
                    {felAccionId === d.id ? '…' : 'Re-certificar'}
                  </button>
                {:else}
                  —
                {/if}
              {:else}
                —
              {/if}
            </td>
          </tr>
        {/each}
        {#if documentos.length === 0}<tr><td colspan="9">Sin documentos.</td></tr>{/if}
      </tbody>
    </table>

    <div class="panel">
      <h3>Consumo de folios FEL (margen)</h3>
      <p class="hint">Cada factura electrónica consume un folio que el certificador nos cobra y que revendemos. Aquí ves el costo, el ingreso y el margen en el período.</p>
      <div class="form">
        <label class="field"><span>Desde</span><input type="date" bind:value={desde} /></label>
        <label class="field"><span>Hasta</span><input type="date" bind:value={hasta} /></label>
        <button onclick={cargarConsumo}>Calcular</button>
      </div>
    </div>
    {#if consumo}
      <div class="resumen">
        <span>Facturas: <strong>{consumo.facturas}</strong></span>
        <span>Costo folios: <strong>Q{consumo.costoFolios}</strong></span>
        <span>Ingreso folios: <strong>Q{consumo.ingresoFolios}</strong></span>
        <span>Margen: <strong>Q{consumo.margen}</strong></span>
      </div>
    {/if}
  {/if}
</section>

<style>
  .filtros-recetas { display: flex; gap: .75rem; flex-wrap: wrap; align-items: flex-end; margin: .5rem 0 1rem; }
  .filtros-recetas .field { display: flex; flex-direction: column; gap: .25rem; }
  .filtros-recetas .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; }
  .inline-chk { display: flex; align-items: center; gap: .35rem; font-size: .85rem; color: #374151; text-transform: none; font-weight: 500; }
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: .5rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; max-width: 62rem; }
  .tabs { display: flex; gap: .35rem; flex-wrap: wrap; margin: .8rem 0 1.2rem; border-bottom: 1px solid #e5e7eb; padding-bottom: .5rem; }
  .tabs button { padding: .5rem .95rem; border: 1px solid #e5e7eb; background: #fff; color: #374151; border-radius: 8px 8px 0 0; cursor: pointer; font: inherit; font-weight: 600; font-size: .85rem; box-shadow: none; }
  .tabs button:hover { background: #f0fdf4; color: #16a34a; transform: none; }
  .tabs button.active { background: #16a34a; border-color: #16a34a; color: #fff; }
  h2 { margin: 0 0 0.2rem; font-size: 1.4rem; color: #111827; }
  h3 { margin: 0 0 .5rem; font-size: 1rem; color: #166534; }
  .hint { color: #6b7280; font-size: .88rem; max-width: 62rem; margin: .2rem 0 .6rem; }
  .err { background: #fee2e2; color: #dc2626; padding: .6rem .8rem; border-radius: 8px; margin: .5rem 0; }
  .ok { background: #dcfce7; color: #166534; padding: .6rem .8rem; border-radius: 8px; margin: .5rem 0; }
  .cols { display: flex; gap: 1.25rem; flex-wrap: wrap; align-items: flex-start; }
  .col { flex: 1; min-width: 20rem; }
  .catalogo-pos { max-width: 26rem; }
  .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem; margin: 0 0 1rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .panel h3 { margin: 0 0 .5rem; }
  .panel-head { display: flex; justify-content: space-between; align-items: center; gap: .5rem; margin-bottom: .6rem; }
  .panel-head input { flex: 1; max-width: 14rem; }
  .form { display: flex; gap: .75rem; flex-wrap: wrap; align-items: flex-end; margin: .6rem 0; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .form input, .form select { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; color: #111827; }
  .form input:focus, .form select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  table { width: 100%; border-collapse: collapse; margin-top: .6rem; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { border-top: 1px solid #f3f4f6; padding: .4rem .8rem; text-align: left; color: #111827; }
  tr.inactiva { color: #9ca3af; }
  input, select { padding: .5rem .65rem; border: 1px solid #e5e7eb; border-radius: 8px; font: inherit; background: #fff; color: #111827; }
  input:focus, select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  input.num { width: 6rem; }
  .catalogo-scroll { max-height: 30rem; overflow-y: auto; margin-top: .4rem; border: 1px solid #f3f4f6; border-radius: 8px; }
  .catalogo-tabla { margin-top: 0; }
  .catalogo-tabla thead th { position: sticky; top: 0; z-index: 1; }
  .catalogo-tabla td { font-size: .85rem; }
  .mono { font-family: monospace; font-weight: 600; color: #15803d; }
  td.num, th.num { text-align: right; }
  td button { padding: .25rem .6rem; font-size: .8rem; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff; color: #16a34a; box-shadow: none; font-weight: 500; }
  td button:hover { background: #f0fdf4; transform: none; }
  .link { background: none; border: 0; color: #16a34a; cursor: pointer; padding: .3rem 0; box-shadow: none; font-weight: 600; }
  .link:hover { transform: none; }
  .resumen { display: flex; gap: 1.5rem; flex-wrap: wrap; background: #f0fdf4; border-left: 4px solid #16a34a; padding: .6rem 1rem; margin-top: .8rem; align-items: center; border-radius: 8px; }
  .resumen strong { color: #166534; }
  .receta-flag { color: #d97706; font-weight: 600; }
  .factura-box { background: #f0fdf4; border: 1px solid #e5e7eb; border-radius: 8px; padding: .6rem .8rem; margin: .6rem 0; }
  .switch { display: flex; align-items: center; gap: .4rem; color: #374151; }
  .acciones { display: flex; gap: .6rem; margin: .4rem 0; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover:not(:disabled) { transform: translateY(-1px); }
  button.primary { padding: .55rem 1.1rem; }
  button.primary:disabled { opacity: .5; cursor: not-allowed; box-shadow: none; }
  button.ghost { background: #fff; color: #16a34a; border: 1px solid #16a34a; box-shadow: none; }
  button.ghost:hover { background: #f0fdf4; transform: none; }
  .cat-bar { align-items: center; margin-bottom: .75rem; }
  .csv-row { display: flex; flex-wrap: wrap; gap: .75rem; align-items: end; margin-top: .75rem; padding-top: .75rem; border-top: 1px dashed #e5e7eb; }
  .csv-hint { font-size: .8rem; color: #6b7280; padding-bottom: .4rem; }
</style>
